import { initialAnnounce } from '@/appLogic/importData/initialData';

import { ISalesResult } from '@/coreDomainsModule/entities/salesResults';
import { IAnnounce } from '@/coreDomainsModule/entities/forecast';
import { IArrival } from '@/coreDomainsModule/entities/arrival';
import { IMvpInventory } from '@/coreDomainsModule/entities/inventory';
import _ from 'lodash';
import { simpleCopy } from '@/utils/deepCopy';
import { splitNumberFormat } from '@/utils/display/numbers';
import {
  ExcelYearMonth,
  TranslateKeyName,
  ExcelTranslateError,
  OutputFileType,
  ExcelOutputResult,
  ExcelTranslateErrorCode,
} from '../../../coreDomainsModule/translateExcel';
import { compareDateForLatestYearMonthData, compareDateForTranslateExcel } from '@/appLogic/translateCompare';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { escapeStringComma, getExcelTypeFromFileName, outputAmountValue, removeNewLineForText } from './excelUtils';
import { FORECAST_INITIAL_VALUE } from '@/coreDomainsModule/constants';

/**
 * ファイル読み込みに関するロジックをまとめる
 */
export class TranslateLogger {
  private latestYearMonthData: ExcelYearMonth;

  /**
   * コンストラクタ
   * @param latestYearMonthData 最新の年月データ
   */
  constructor(latestYearMonthData: ExcelYearMonth) {
    this.latestYearMonthData = latestYearMonthData;
  }

  /**
   * 未転記取引まとめ.csvに出力する内示情報(announce)の配列長を決定する
   * fetchLatestMonthsForTranslateArray()に配列長が0で渡ると、
   * ファイルがない場合に配列を空で埋めることができなくなるのでその対処
   * @param announceArrLength announce配列の長さ
   * @returns 配列の長さを返す(latestYearMonthData.announceの長さが0なら1を返す)
   */
  determineAnnounceArrayLength = (announceArrLength: number): number => {
    // latestYearMonthData.announceの長さが0の時は1、それ以外は配列長をそのまま返す
    if (announceArrLength == 0) return 1;
    return announceArrLength;
  };

  //ファイルタイプと年月を結合して、新しい列名を作成する
  //内示情報のみに対応
  combineAnnounceTitleAndMonth = (fileType: string, latestYearMonthData: ExcelYearMonth): string[] => {
    const outputRowNames: string[] = [];
    // 内示エクセルを取り込んでいない場合の処理
    if (latestYearMonthData.customerAnnounce.length === 0) {
      outputRowNames.push(fileType);
      return outputRowNames;
    }
    const sortedYearMonthArray: string[] = [...latestYearMonthData.customerAnnounce].sort(compareDateForLatestYearMonthData(true));
    // 取り込んだ内示情報の月数だけ、'内示'+年月を結合した列名を作成
    sortedYearMonthArray.forEach((yearMonth) => {
      const announceYear = yearMonth.split('_')[0] + '/';
      const announceMonth = DateService.convertMonthWithZero(yearMonth.split('_')[1]) + ' ';
      const newRowName = announceYear + announceMonth + fileType;
      outputRowNames.push(escapeStringComma(newRowName));
    });
    return outputRowNames;
  };

  // 転記元の対象配列（announce, salesResults, mvpInventory等）の、最新情報（直近nヶ月）を取得する
  private fetchLatestMonthsForTranslateArray = (
    fileNames: string,
    numberOfGetMonth: number,
    addTimeInformationAtTheEnd: boolean,
    translateArr?: IAnnounce[] | ISalesResult[] | IMvpInventory[] | IArrival[],
    translateArrType?: TranslateKeyName
  ): Array<string> => {
    const fileType = getExcelTypeFromFileName(fileNames);
    // ファイル名とtranslateArrTypeが一致していない場合＝取得対象外のため、指定の枠数（numberOfGetMonth）空で埋めて終わり
    if (fileType !== translateArrType || !translateArr || translateArr.length === 0) {
      return new Array(numberOfGetMonth).fill('');
    }
    let translateTmpArr = simpleCopy(translateArr);
    const latestMonthsForTranslateArray: Array<string> = [];
    //announceが無い、またはfileTypeと一致しないエクセルファイルの時はセルを空にする
    // 販売実績、入荷の情報は取引数量が1以上の取引のみの配列作成の処理を行う
    if (translateArrType === '販売実績' || translateArrType === '入荷') {
      // 取引数量が1以上の取引のみの配列を作成する
      translateTmpArr = Object.assign(
        [],
        translateTmpArr.filter((val) => val.amount && val.amount !== 0)
      );
      // 取引数量が全ての年月で0または登録がない場合、`実績なし`とする
      if (translateTmpArr.length == 0) {
        latestMonthsForTranslateArray.push(removeNewLineForText(String('実績なし')));
        return latestMonthsForTranslateArray;
      }
    }
    // 降順に並び替えて、１番目から必要な分(numberOfGetMonth)だけ取得していく
    translateTmpArr.sort(compareDateForTranslateExcel(true));
    // 在庫、内示以外の場合、最新年月から読み取って、1以上の数量取引されている年月の配列にする
    for (const [index, translateObj] of translateTmpArr.entries()) {
      if (index >= numberOfGetMonth) continue;
      if (addTimeInformationAtTheEnd) {
        const yearMonth = translateObj.year.toString() + '/' + DateService.convertMonthWithZero(translateObj.month);
        // 取引数量と年月を結合した文字列を作成する
        const processingAmount = outputAmountValue(translateObj.amount) + ' (' + yearMonth + ')';
        latestMonthsForTranslateArray.push(removeNewLineForText(escapeStringComma(processingAmount)));
      } else {
        latestMonthsForTranslateArray.push(removeNewLineForText(escapeStringComma(outputAmountValue(translateObj.amount))));
      }
    }
    // 必要分に満たなかった分は、空文字の要素で埋める
    const shortagePropertyCount = numberOfGetMonth - latestMonthsForTranslateArray.length;
    [...Array(shortagePropertyCount)].map(() => latestMonthsForTranslateArray.push(''));

    return latestMonthsForTranslateArray;
  };

  /**
   * 未転記取引まとめに使用するannounce配列の整形を行い、整形後のannounce配列を返す
   * announce配列の長さ、年月順序をlatestYearMonthDataに合わせることを主に行う
   * @param latestYearMonthData 最新の年月情報
   * @param inputAnnounce 内示情報
   * @returns 整形した内示情報またはundefinedを返す
   */
  private outputAnnounceFormatter = (latestYearMonthData: ExcelYearMonth, inputAnnounce?: IAnnounce[]): IAnnounce[] | undefined => {
    if (!inputAnnounce) return; // 内示が登録されていなければ抜ける
    const sortedYearMonthArray = [...latestYearMonthData.customerAnnounce].sort(compareDateForLatestYearMonthData(true)); // 年月を降順にソート
    const outputAnnounce: IAnnounce[] = []; // 出力用変数
    // 出力用変数をinitialAnnounceで初期化
    const initialAnnounce: IAnnounce = {
      amount: FORECAST_INITIAL_VALUE,
      year: 0,
      month: 0,
      manageInitiatedYearMonth: undefined,
      createAt: undefined,
      updateAt: undefined,
    };
    for (let i = 0; i < sortedYearMonthArray.length; i++) {
      outputAnnounce.push(simpleCopy({ ...initialAnnounce }));
    }
    // 年月をkey、indexをvalueとしたobjectの作成
    // これを元にannounce配列を整形する
    const sortedYearMonthObject = sortedYearMonthArray.reduce((obj, value: string, index: number) => {
      obj[value] = index;
      return obj;
    }, {} as { [key: string]: number });

    // sortedYearMonthObjectを順番に走査して、keyが存在(エクセルから読み取った内示情報に年月がある)ときは、出力変数に内示情報を登録する
    // 無い場合は年月のみ登録する
    for (const key in sortedYearMonthObject) {
      for (const announce of inputAnnounce) {
        const year = announce.year;
        const month = announce.month;
        const checkKey = String(year) + '_' + String(month); // エクセルから読み取った年月
        const objIndex = sortedYearMonthObject[checkKey]; // outputAnnounceに登録するindexの決定
        // outputAnnounceに登録する内示情報の決定
        if (objIndex !== undefined && checkKey === key) {
          outputAnnounce[objIndex].year = announce.year;
          outputAnnounce[objIndex].month = announce.month;
          // 数量が入っていれば(0を含む)値を登録、違う場合('-'など)は登録しない
          if (announce.amount || announce.amount === 0) {
            outputAnnounce[objIndex].amount = announce.amount;
          }
          break;
        }
      }
      // inputAnnounceに年月が存在しない場合に年月のみoutputAnnounceに登録する
      const checkIndex = sortedYearMonthObject[key];
      if (outputAnnounce[checkIndex].year === 0 || outputAnnounce[checkIndex].month === 0) {
        const fixYear = key.split('_')[0];
        const fixMonth = key.split('_')[1];
        outputAnnounce[checkIndex].year = Number(fixYear);
        outputAnnounce[checkIndex].month = Number(fixMonth);
      }
    }
    return outputAnnounce;
  };

  /**
   * csv出力を行う転記時のエラー(未転記取引情報、重複取引情報など)をまとめる
   * csvごとに出力項目が違うので、エラーコード別で処理を行うようにしている
   * @param translateErrorLog エクセルの転記時のエラー情報をまとめるオブジェクト
   * @param errObj エラーオブジェクト
   * @param errCode Excelに転記する時のエラーの一覧(エラーコード)
   * @param fileType 出力するエクセルの種類
   */
  createErrorLogData = (
    translateErrorLog: string[][],
    errObj: ExcelTranslateError[],
    errCode: ExcelTranslateErrorCode,
    fileType?: OutputFileType
  ): void => {
    // デフォルトは在庫表のエラー項目にする
    if (!fileType) fileType = '在庫表';
    switch (errCode) {
      case '転記先が存在しない':
        // TODO: 出力csvファイルの列名を別ファイルに分けたい
        translateErrorLog.push([
          'エラー名',
          '対象ファイル',
          'シート名',
          '対象セル',
          '置き場',
          '商品CD',
          '商品名',
          '容量',
          '仕入先名',
          '売上先名',
          '送り先名',
          ...this.combineAnnounceTitleAndMonth('内示', this.latestYearMonthData),
          '販売実績',
        ]);
        if (fileType === '在庫表') {
          // 列名は配列必ず配列の先頭になるので、そこに項目追加
          translateErrorLog[0].push('入荷', '在庫');
        }
        errObj.forEach((err) => {
          // シート名が存在しない場合は空埋め
          if (!err.errorSheetName) {
            err.errorSheetName = {
              inputSheetName: '',
            };
          }
          const inputFileType = getExcelTypeFromFileName(err.errorFileName.inputFileName);
          if (fileType === '仕入先内示' && inputFileType !== '内示' && inputFileType !== '販売実績') return;
          translateErrorLog.push([
            escapeStringComma(err.errCode),
            escapeStringComma(err.errorFileName.inputFileName),
            removeNewLineForText(String(err.errorSheetName?.inputSheetName ?? '')),
            escapeStringComma(err.errorCellNumber.inputCellNumber),
            removeNewLineForText(escapeStringComma(String(err.value?.warehouse))),
            removeNewLineForText(escapeStringComma(String(err.value?.businessNumber))),
            removeNewLineForText(escapeStringComma(String(err.value?.businessName))),
            removeNewLineForText(escapeStringComma(String(err.value?.package))),
            removeNewLineForText(escapeStringComma(String(err.value?.supplierName))),
            removeNewLineForText(escapeStringComma(String(err.value?.customerName))),
            removeNewLineForText(escapeStringComma(String(err.value?.salesDestinationName))),
            ...this.fetchLatestMonthsForTranslateArray(
              err.errorFileName.inputFileName,
              this.determineAnnounceArrayLength(this.latestYearMonthData.customerAnnounce.length),
              false,
              this.outputAnnounceFormatter(this.latestYearMonthData, err.value?.customerAnnounce),
              '内示'
            ),
            ...this.fetchLatestMonthsForTranslateArray(err.errorFileName.inputFileName, 1, true, err.value?.salesResults, '販売実績'),
          ]);
          if (fileType === '在庫表') {
            translateErrorLog[translateErrorLog.length - 1].push(
              ...this.fetchLatestMonthsForTranslateArray(err.errorFileName.inputFileName, 1, true, err.value?.arrivals, '入荷'),
              ...this.fetchLatestMonthsForTranslateArray(err.errorFileName.inputFileName, 1, true, err.value?.mvpInventory, '在庫')
            );
          }
        });
        break;
      case '取引情報が重複している':
        translateErrorLog.push([
          'エラー名',
          '対象ファイル1',
          'ファイル1シート名',
          'ファイル1対象セル',
          '対象ファイル2',
          'ファイル2シート名',
          'ファイル2対象セル',
        ]);
        errObj.forEach((err) => {
          // シート名が存在しない場合は空埋め
          if (!err.errorSheetName) {
            err.errorSheetName = {
              inputSheetName: '',
              registerSheetName: '',
            };
          }
          translateErrorLog.push([
            escapeStringComma(err.errCode),
            escapeStringComma(err.errorFileName.inputFileName),
            removeNewLineForText(String(err.errorSheetName.inputSheetName)),
            escapeStringComma(err.errorCellNumber.inputCellNumber),
            escapeStringComma(err.errorFileName.registerFileName ?? ''),
            removeNewLineForText(String(err.errorSheetName.registerSheetName)),
            escapeStringComma(err.errorCellNumber.registerCellNumber ?? ''),
          ]);
        });
        break;
    }
  };

  /**
   * 変更履歴情報の作成を行う
   * 変更履歴には変更前と変更後の数量差分を含む
   * @param outputExcelAddressInfo Excelに転記する情報を転記するセルアドレスごとにまとめたもの
   * @param sheetName シート名
   * @param cellAddress 転記先セル番号
   */
  createTranslateLog = (outputExcelAddressInfo: ExcelOutputResult, sheetName: string, cellAddress: string): string[] => {
    // 転記先の列名
    const columnName = outputAmountValue(outputExcelAddressInfo[cellAddress]?.columnName ?? '');
    let prevValue = 0; // 転記前の取引数量
    let translateValue = 0; // 転記後の取引数量
    let transactionInfo: string[] = []; // 転記される取引情報
    // 転記前の取引数量の取得
    if (outputExcelAddressInfo[cellAddress]?.prevValue) {
      prevValue = String(outputExcelAddressInfo[cellAddress]?.prevValue).parseNumber(); // カンマを排除
    }
    // 転記後の取引数量の取得
    if (outputExcelAddressInfo[cellAddress].value) {
      translateValue = Number(outputExcelAddressInfo[cellAddress].value);
    }
    // 転記される取引情報を取得
    if (outputExcelAddressInfo[cellAddress].transactionInfo) {
      transactionInfo = outputExcelAddressInfo[cellAddress].transactionInfo as string[];
    } else {
      transactionInfo = ['', '', '', '', '', ''];
    }
    // 返す配列の作成処理
    // 取引重複時、差分計算ができなくなるのでそれ用
    const returnArray: string[] = [
      escapeStringComma(sheetName),
      escapeStringComma(cellAddress),
      ...transactionInfo,
      escapeStringComma(columnName),
      escapeStringComma(splitNumberFormat(outputAmountValue(prevValue)) ?? ''),
    ];
    if (outputExcelAddressInfo[cellAddress].isDuplicatedBusiness) {
      // 重複している取引の場合は、警告文を出力する
      returnArray.push(escapeStringComma(String(outputExcelAddressInfo[cellAddress].value)), escapeStringComma('-'));
    } else {
      // 重複している取引でない場合は、転記後の数量と差分を出力する
      returnArray.push(
        escapeStringComma(splitNumberFormat(outputAmountValue(translateValue)) ?? ''),
        escapeStringComma(splitNumberFormat(outputAmountValue(translateValue - prevValue)) ?? '')
      );
    }
    // 変更履歴情報を追加
    return returnArray;
  };
}
